<template>
  <div class="columns is-marginless py-8 statistics">
    <b-loading :active="loading" />
    <div class="column has-background-white px-4 py-3 w-full">
      <router-link class="prev-page" to="/statistics/members">
        <font-awesome-icon class="mr-2" icon="chevron-left" />
        <span>返回訂購列表</span>
      </router-link>
      <div class="flex gap-4 items-baseline">
        <h1 class="font-medium has-text-black is-size-5 mb-6 mt-4">
          人員詳情
        </h1>
        <span class="text-gray-500">{{ dateTypeText }}：{{ dateRange }}</span>
      </div>
      <section
        class="is-flex is-flex-wrap-wrap gap-1 mb-2 px-[24px] py-4 statistics-details__infos"
      >
        <div
          v-for="(info, infoIndex) in infoColumns"
          :key="`info-${infoIndex}-${info.title}`"
          :class="{ 'is-align-items-center': info.centered }"
          class="is-flex is-flex-direction-column statistics-details__infos--section"
        >
          <label class="font-small has-text-grey mb-2">{{ info.title }}</label>
          <span :class="info.classes" class="is-size-6">{{ info.value }}</span>
        </div>
      </section>
      <div class="flex items-center justify-end mb-2">
        <w-button
          :disabled="disableExport"
          :loading="isExporting"
          color-type="secondary"
          size="func"
          type="link"
          @click="exportReport"
        >
          <span class="flex items-center">
            <i
              :class="disableExport ? 'text-gray-300' : 'text-secondary-500'"
              class="icon-cloud-download mr-2 text-lg"
            />
            匯出報表
          </span>
        </w-button>
      </div>
      <b-table
        :data="formattedRecords"
        :row-class="itemRowClass"
        :mobile-cards="false"
        :opened-detailed="openedDetails"
        :show-detail-icon="false"
        class="details-table"
        custom-detail-row
        detail-key="index"
        detailed
        hoverable
        scrollable
      >
        <b-table-column
          :th-attrs="columnThAttrs"
          field="id"
          header-class="is-size-7"
          label="No."
          width="28px"
          v-slot="props"
        >
          {{ props.row.index + 1 + (page - 1) * perPage }}.
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="startTime"
          header-class="is-size-7 min-w-[104px]"
          label="開放訂購日"
          v-slot="props"
          width="108px"
        >
          {{ getDate(props.row.startTime) }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="receiveTime"
          header-class="is-size-7 min-w-[104px]"
          label="領取起始日"
          v-slot="props"
          width="108px"
        >
          {{ props.row.receiveTime }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="id"
          header-class="is-size-7"
          label="訂購ID"
          v-slot="props"
          width="48px"
        >
          {{ props.row.id }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="orderName"
          header-class="is-size-7 min-w-[120px]"
          label="訂購名稱"
          v-slot="props"
          width="108px"
        >
          <section class="is-align-items-baseline is-flex is-justify-content-space-between">
            <span class="font-small">{{ props.row.orderName }}</span>
          </section>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="status"
          header-class="is-size-7 min-w-[80px]"
          label="訂購狀態"
          v-slot="props"
          width="108px"
        >
          <span :class="groupBuyingStatusColor(props.row)">{{ groupBuyingStatus(props.row) }}</span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="orderCount"
          header-class="is-size-7"
          label="訂單數"
          numeric
          v-slot="props"
          width="108px"
        >
          {{ props.row.orderCount | commaFormat }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="total"
          header-class="is-size-7"
          label="訂單金額"
          numeric
          v-slot="props"
          width="104px"
        >
          {{ props.row.total | commaFormat | priceFormat }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="unpaid"
          header-class="is-size-7"
          label="未付款金額"
          numeric
          v-slot="props"
          width="104px"
        >
          {{ props.row.unpaid | commaFormat | priceFormat }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="subsidyTotal"
          header-class="is-size-7"
          label="每人補助金額"
          numeric
          v-slot="props"
          width="104px"
        >
          {{ props.row.subsidyTotal | commaFormat | priceFormat }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="outOfSubsidy"
          header-class="is-size-7"
          label="自付額"
          numeric
          v-slot="props"
          width="104px"
        >
          {{ props.row.outOfSubsidy | commaFormat | priceFormat }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="paymentStatus"
          header-class="is-size-7 min-w-[80px]"
          label="付款狀態"
          v-slot="props"
          width="56px"
        >
          <span :class="getPaymentStatusColor(props.row)">{{ getPaymentStatus(props.row) }}</span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="receiveStatus"
          header-class="is-size-7 min-w-[88px]"
          label="領取狀態"
          v-slot="props"
          width="117px"
        >
          <span :class="getReceiveStatusColor(props.row)">
            {{ getReceiveStatus(props.row) }}
          </span>
          <span
            v-if="isReceived(props.row)"
            class="text-gray-500 text-xs"
          >
           {{ getReceivingTimeText(props.row) }}
          </span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          centered
          field="onSiteOrder"
          header-class="is-size-7"
          label="現場訂購"
          v-slot="props"
          width="48px"
        >
          <i
            v-if="props.row['onSiteOrder']"
            class="icon-check-thin text-xl text-red-500"
          />
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="action"
          label="訂購內容"
          centered
          header-class="is-size-7 min-w-[80px] py-2"
          v-slot="props"
          width="108px"
        >
          <span class="cursor-pointer font-small hover:text-blue-500 text-blue-400"
            @click.prevent="props.toggleDetails(props.row)">
              {{ openedDetails.includes(props.row.index) ? '收合' : '展開' }}
          </span>
        </b-table-column>

        <template #detail="props">
          <tr>
            <td class="p-0" colspan="15">
              <table cellpadding="6" class="detail-table">
                <thead>
                  <tr>
                    <th width="60px">&nbsp;</th>
                    <th>數量</th>
                    <th colspan="2">品項</th>
                    <th colspan="2">備註</th>
                    <th>單價</th>
                    <th>小計</th>
                    <th width="280px">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, itemIndex) in getOrderDetails(props.row.items)"
                    :key="`row-${props.row.id}-item-${itemIndex}`"
                  >
                    <td width="54px" />
                    <td class="text-primary-700">
                      {{ item.quantity || '0' }}
                    </td>
                    <td colspan="2">
                      <span class="text-primary-700" :title="item.name">
                        {{ item.name }}
                      </span>
                    </td>
                    <td colspan="2">
                      <template v-for="option in item.options">
                        <span
                          v-for="optionItem in option.items"
                          :key="`option-item-${optionItem.index}`"
                          class="
                            bg-blue-50
                            border
                            border-blue-100
                            last:mr-1
                            leading-[22px]
                            mr-1
                            px-[10px]
                            py-[1px]
                            rounded-full
                            whitespace-nowrap
                          "
                        >
                          {{ optionItem.name }}
                          {{ optionItem.price ? formatPrice(optionItem.price) : '' }}
                        </span>
                      </template>
                      <span v-if="item.note">#{{ item.note }}</span>
                    </td>
                    <td class="text-primary-700">{{ item.price | commaFormat | priceFormat }}</td>
                    <td class="text-primary-700">
                      {{ item.subtotal | commaFormat | priceFormat }}
                    </td>
                    <td width="54px" />
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        </template>

        <template #empty>
          <div v-if="!loading" class="my-5 has-text-centered">
            目前沒有任何訂購喔！
          </div>
        </template>
      </b-table>
      <div v-if="total && !loading" class="flex gap-4 items-center">
        <b-select v-model="perPage">
          <option
            v-for="option in perPageOptions"
            :value="option.value"
            :key="option.label"
          >
            {{ option.label }}
          </option>
        </b-select>
        <div class="flex-grow">
          <b-pagination
            :total="total"
            v-model="page"
            range-before="3"
            range-after="1"
            nextIcon="right-chevron"
            prevIcon="left-chevron"
            :per-page="perPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MemberDetails from './MemberDetails';

export default MemberDetails;
</script>

<style lang="scss" scoped>
@import 'MemberDetails';
</style>
